import { useCallback, useMemo, useRef, useState } from 'react';
import { formatRoute, rangeToggle, type PlaylistPrivacy } from '@visious/core';
import { navigate } from '../../router/router';
import { systemClock } from '../../platform/clock';
import { describeWriteProblem } from '../../playlists/model';
import { useCreatePlaylist, useEditPlaylist } from '../../playlists/mutations';
import { rememberPlaylistPrivacy, useKnownPlaylistPrivacy } from '../../playlists/privacyMemory';
import { useOwnedPlaylists } from '../../playlists/useOwnedPlaylists';
import { text } from '../../strings';
import type { ProblemCopy } from '../../api/problem';
import {
  playlistsFormState,
  playlistsScreenState,
  type PlaylistsFormState,
  type PlaylistsScreenState,
} from './model';

/**
 * The React binding for §8's Playlists page.
 *
 * `enabled: true` on the owned-playlists query, unlike the Watch menu's: here the list *is* the page, so
 * the cost the endpoint imposes (`useOwnedPlaylists.ts`) is unavoidable and paying it on arrival is
 * correct rather than eager.
 *
 * The privacy a create was made with is remembered in a **module store** rather than here, and that is
 * the one design note worth reading: no response carries `privacy`, so what this app sent is the only
 * exact source — and the create navigates straight to the playlist page, which has to read the same
 * fact. Held in this hook's state it was lost on the way (`privacyMemory.ts` records that pass).
 */
export interface PlaylistsBinding {
  readonly state: PlaylistsScreenState;
  readonly form: PlaylistsFormState;
  readonly setTitle: (title: string) => void;
  readonly setPrivacy: (privacy: PlaylistPrivacy) => void;
  readonly submit: () => void;
  readonly retry: () => void;
  readonly refreshing: boolean;
  /**
   * Manage mode. Component state rather than the URL: a selection is not addressable, not
   * shareable, and Back must not "undo" it — the same reasoning as the create form's title.
   */
  readonly manage: boolean;
  readonly toggleManage: () => void;
  readonly selected: ReadonlySet<string>;
  /** `range` is a held Shift (2026-08-02): the previously toggled card to this one, inclusive. */
  readonly toggleSelect: (playlistId: string, range: boolean) => void;
  readonly clearSelection: () => void;
  /** The grid search — display-only, so it is state here and never in the URL. */
  readonly search: string;
  readonly setSearch: (value: string) => void;
  /** Serial over the selection; the view confirms deletion first (a Modal), this just does it. */
  readonly deleteSelected: () => void;
  readonly setPrivacySelected: (privacy: PlaylistPrivacy) => void;
  /** `null` while nothing runs; otherwise progress for the manage bar's own words. */
  readonly working: { readonly done: number; readonly total: number } | null;
  readonly manageProblem: ProblemCopy | null;
}

export function usePlaylists(): PlaylistsBinding {
  const owned = useOwnedPlaylists(true);
  const { create, creating } = useCreatePlaylist();
  const edit = useEditPlaylist();

  const [title, setTitle] = useState('');
  const [privacy, setPrivacy] = useState<PlaylistPrivacy>('private');
  const [problem, setProblem] = useState<ProblemCopy | null>(null);
  const [manage, setManage] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(NO_SELECTION);
  /** The last card toggled — shift-click's other end. A ref: it must never re-render anything. */
  const selectAnchor = useRef<string | null>(null);
  const [working, setWorking] = useState<{ done: number; total: number } | null>(null);
  const [manageProblem, setManageProblem] = useState<ProblemCopy | null>(null);
  // A module store rather than this hook's state: the playlist page has to read the same fact, and it
  // is reached by a navigation from here (`privacyMemory.ts` records the browser pass that showed it).
  const knownPrivacy = useKnownPlaylistPrivacy();

  const form = useMemo(
    () => playlistsFormState({ title, privacy, problem, busy: creating }),
    [creating, privacy, problem, title],
  );

  const submit = useCallback(() => {
    if (form.field.value === null || creating) return;
    const chosen = privacy;
    void (async () => {
      setProblem(null);
      const result = await create({ title: form.field.value ?? '', privacy: chosen });
      if (!result.ok) {
        setProblem(describeWriteProblem(result.problem, 'create'));
        return;
      }
      setTitle('');
      // Exact, because we are the one who sent it — and no read will ever say so again.
      rememberPlaylistPrivacy(result.value.playlistId, chosen);
      /*
       * Straight to the new playlist, which is also the answer to a small ordering surprise: the list
       * endpoint returns playlists in **creation order**, so a new one lands at the *bottom* of a long
       * page. Navigating means the reader sees what they made instead of hunting for it — and the
       * playlist page is where the rest of the editing lives anyway.
       */
      navigate(formatRoute({ kind: 'playlist', playlistId: result.value.playlistId }));
    })();
  }, [create, creating, form.field.value, privacy]);

  const state = useMemo(
    () =>
      playlistsScreenState({
        playlists: owned.playlists,
        droppedCount: owned.droppedCount,
        problem: owned.problem,
        nowEpochSec: Math.floor(systemClock.nowEpochMs() / 1000),
        knownPrivacy,
        search,
      }),
    // `systemClock.nowEpochMs()` is read inside the memo rather than being a dependency (2b's rule, and
    // `useSubscriptions.ts` does the same): it changes every render and only decides a relative date,
    // which does not need second resolution. Recomputing on it would defeat the memo entirely.
    [knownPrivacy, owned.droppedCount, owned.playlists, owned.problem, search],
  );

  /**
   * One serial pass over the selection, shared by both bulk acts. Serial for the manage-subs page's
   * reason: each write is one small request against one machine, and a progress count that means
   * something beats finishing marginally sooner. The selection is cleared either way — what failed is
   * back in the refreshed list, and the failure notice says re-selecting it is the retry.
   */
  const runOverSelection = useCallback(
    (act: (playlistId: string) => Promise<boolean>) => {
      const ids = [...selected];
      if (ids.length === 0 || working !== null) return;
      setManageProblem(null);
      setWorking({ done: 0, total: ids.length });

      void (async () => {
        let failures = 0;
        try {
          for (const [index, playlistId] of ids.entries()) {
            if (!(await act(playlistId))) failures += 1;
            setWorking({ done: index + 1, total: ids.length });
          }
        } finally {
          setWorking(null);
          setSelected(NO_SELECTION);
          selectAnchor.current = null;
          if (failures > 0) setManageProblem(text().playlists.page.manage.failed);
          owned.refetch();
        }
      })();
    },
    [owned, selected, working],
  );

  const deleteSelected = useCallback(() => {
    runOverSelection(async (playlistId) => (await edit.remove(playlistId)).ok);
  }, [edit, runOverSelection]);

  const setPrivacySelected = useCallback(
    (next: PlaylistPrivacy) => {
      runOverSelection(async (playlistId) => {
        /*
         * The PATCH must re-send the **raw** title — mandatory on every call (§7i) — so it comes from
         * the playlist record, never from the display row, whose "Untitled playlist" stand-in would
         * silently rename a titleless one.
         */
        const playlist = owned.playlists?.find((candidate) => candidate.playlistId === playlistId);
        if (playlist === undefined) return false;
        const result = await edit.update(playlistId, { title: playlist.title, privacy: next });
        // Exact, because we are the one who sent it — no read will ever report `privacy` back.
        if (result.ok) rememberPlaylistPrivacy(playlistId, next);
        return result.ok;
      });
    },
    [edit, owned.playlists, runOverSelection],
  );

  return {
    state,
    form,
    setTitle: (next: string) => {
      setTitle(next);
      // A refusal is about the value that was sent, so it stops being true the moment the value changes.
      if (problem !== null) setProblem(null);
    },
    setPrivacy,
    submit,
    retry: () => void owned.refetch(),
    refreshing: owned.refreshing,
    manage,
    toggleManage: useCallback(() => {
      setManage((previous) => !previous);
      // Leaving manage mode drops the selection and its failure notice — both are about the mode.
      setSelected(NO_SELECTION);
      selectAnchor.current = null;
      setManageProblem(null);
    }, []),
    selected,
    toggleSelect: useCallback(
      (playlistId: string, range: boolean) => {
        // The displayed rows, not `owned`: a shift-range is a claim about what the reader sees,
        // and the grid search may have narrowed it (`rangeToggle`'s contract).
        const order = state.kind === 'ready' ? state.rows.map((row) => row.playlistId) : [];
        // Read before `setSelected`, never inside the updater: the updater runs at flush time,
        // after the anchor has already moved onto the clicked key (measured on the entry grid —
        // every shift-range degraded to a plain toggle).
        const anchor = selectAnchor.current;
        selectAnchor.current = playlistId;
        setSelected((previous) => rangeToggle(previous, order, anchor, playlistId, range));
      },
      [state],
    ),
    clearSelection: useCallback(() => {
      setSelected(NO_SELECTION);
      selectAnchor.current = null;
    }, []),
    search,
    setSearch,
    deleteSelected,
    setPrivacySelected,
    working,
    manageProblem,
  };
}

const NO_SELECTION: ReadonlySet<string> = new Set<string>();
